import { inject, Injectable, makeEnvironmentProviders } from '@angular/core';
import { LocalStorageType } from '@core/enums';
import { SKY_BROWSER_STORAGE, SKY_LOCAL_STORAGE_TOKEN } from '@core/tokens';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private readonly storage: Storage = inject(SKY_BROWSER_STORAGE);

  /**
   * Add item to local storage
   * @param key A unique key of `LocalStorageType` type.
   * @param value Item value.
   *
   */
  setItem(key: LocalStorageType | string, value: unknown): void {
    const valueJSON = JSON.stringify(value);

    if (null == valueJSON) {
      throw Error(`The ${value} has occur with empty value, please use removeItem instead.`);
    }

    this.storage.setItem(key, valueJSON);
  }

  /**
   * Get item from local storage
   * @param key A unique key of `LocalStorageType` type.
   *
   */
  getItem(key: LocalStorageType | string): string | null {
    return this.storage.getItem(key);
  }

  /**
   * Get item from local storage parsed
   * @param key A unique key of `LocalStorageType` type.
   *
   */
  getItemParsed<T>(key: LocalStorageType | string): T | null {
    const item = this.getItem(key);
    return item != null ? JSON.parse(item) : null;
  }

  /**
   * Check if item is exist in local storage
   * @param key A unique key of `LocalStorageType` type.
   *
   */
  hasItem(key: LocalStorageType | string): boolean {
    return key in this.storage;
  }

  /**
   * Remove item from local storage
   * @param key A unique key of `LocalStorageType` type.
   *
   */
  removeItem(key: LocalStorageType | string): void {
    this.storage.removeItem(key);
  }
}

export const localStorageProvider = {
  provide: SKY_LOCAL_STORAGE_TOKEN,
  useClass: LocalStorageService,
};

export function provideLocalStorage() {
  return makeEnvironmentProviders([LocalStorageService, localStorageProvider]);
}
